import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { DeskTable, muted, type Column } from '@/components/common/DeskTable'
import FilterBarApply from '@/components/common/FilterBarApply'
import FilterBarForm from '@/components/common/FilterBarForm'
import PageFrame from '@/components/common/PageFrame'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { GlassSelect } from '@/components/ui/glass-select'
import { backofficeApi, type DailyAccsumRow, type DailyFundsRow } from '@/lib/api-client'
import { fmtDate, fmtNum, last7Days } from '@/lib/format'
import { bookLabel } from '@/lib/labels'
import { FILTER_BAR } from '@/lib/ui-shell'
import { PlValue } from '@/pages/AccSumPage'

type Range = { from: string; to: string; book: string }
function rangeFromSearch(sp: URLSearchParams): Range {
  const days = last7Days()
  return { from: sp.get('from') || days.from, to: sp.get('to') || days.to, book: sp.get('book') || 'mt5' }
}

const MAX_DAYS = 31
// Queries stay off while the range is invalid, so the tables must not read as "no data".
const BAD_RANGE_TITLE = `Pick a date range of at most ${MAX_DAYS} days`
const BAD_RANGE_HINT = 'Fix the dates above and apply them to load this table.'
const dayCount = (r: Range) => (Date.parse(r.to) - Date.parse(r.from)) / 86_400_000 + 1
const money = (v: unknown) => muted(fmtNum(v))
const signed = (v: unknown) => <PlValue value={v as string} />
const count = (n: number, sum: string) => (n ? `${n} · ${fmtNum(sum)}` : '—')

const fundsColumns: Column<DailyFundsRow>[] = [
  { key: 'date', header: 'Date', sticky: true, mobile: 'title', sortFirst: 'desc', sortValue: (r) => r.date, cell: (r) => fmtDate(r.date) },
  { key: 'ccy', header: 'Currency', mobile: 'subtitle', sortValue: (r) => r.currency, cell: (r) => r.currency || '—' },
  { key: 'net', header: 'Net', headerTitle: 'Deposits minus withdrawals, in that currency', align: 'right', mobile: 'amount', sortFirst: 'desc', sortValue: (r) => Number(r.net), cell: (r) => signed(r.net) },
  { key: 'dep', header: 'Deposits', headerTitle: 'Count · total', align: 'right', mobile: 'field', sortFirst: 'desc', sortValue: (r) => Number(r.deposit_sum), cell: (r) => muted(count(r.deposit_count, r.deposit_sum)) },
  { key: 'wd', header: 'Withdrawals', headerTitle: 'Count · total', align: 'right', mobile: 'field', sortFirst: 'desc', sortValue: (r) => Number(r.withdraw_sum), cell: (r) => muted(count(r.withdraw_count, r.withdraw_sum)) },
  { key: 'tr', header: 'Transfers', headerTitle: 'Count · total', align: 'right', mobile: 'field', sortFirst: 'desc', sortValue: (r) => Number(r.transfer_sum), cell: (r) => muted(count(r.transfer_count, r.transfer_sum)) },
]

const accsumColumns: Column<DailyAccsumRow>[] = [
  { key: 'date', header: 'Date', sticky: true, mobile: 'title', sortFirst: 'desc', sortValue: (r) => r.trade_date, cell: (r) => fmtDate(r.trade_date) },
  { key: 'equity', header: 'Equity', align: 'right', mobile: 'amount', sortFirst: 'desc', sortValue: (r) => Number(r.equity), cell: (r) => money(r.equity) },
  { key: 'accounts', header: 'Accounts', align: 'right', mobile: 'field', sortFirst: 'desc', sortValue: (r) => r.accounts, cell: (r) => fmtNum(r.accounts) },
  { key: 'min', header: 'Margin in', align: 'right', mobile: 'field', sortFirst: 'desc', sortValue: (r) => Number(r.margin_in), cell: (r) => money(r.margin_in) },
  { key: 'mout', header: 'Margin out', align: 'right', mobile: 'field', sortFirst: 'desc', sortValue: (r) => Number(r.margin_out), cell: (r) => money(r.margin_out) },
  { key: 'tpl', header: 'Trading P/L', align: 'right', sortFirst: 'desc', sortValue: (r) => Number(r.trading_pl), cell: (r) => signed(r.trading_pl) },
  { key: 'dpl', header: 'Daily P/L', align: 'right', sortFirst: 'desc', sortValue: (r) => Number(r.daily_pl), cell: (r) => signed(r.daily_pl) },
  { key: 'lot', header: 'Lots', headerTitle: 'Today lot', align: 'right', sortFirst: 'desc', sortValue: (r) => Number(r.today_lot), cell: (r) => money(r.today_lot) },
]

export function DailyPage() {
  const [sp, setSp] = useSearchParams()
  const initial = rangeFromSearch(sp)
  const [draft, setDraft] = useState<Range>(initial)
  const [applied, setApplied] = useState<Range>(initial)
  useEffect(() => {
    if (sp.get('from') && sp.get('to') && sp.get('book')) return
    const next = rangeFromSearch(sp)
    const cur = new URLSearchParams(sp)
    cur.set('from', next.from)
    cur.set('to', next.to)
    cur.set('book', next.book)
    setSp(cur, { replace: true })
  }, [sp, setSp])
  const booksQuery = useQuery({ queryKey: ['books'], queryFn: () => backofficeApi.listBooks() })
  const books = booksQuery.data ?? []

  const rangeReady = Boolean(applied.from && applied.to && applied.from <= applied.to && dayCount(applied) <= MAX_DAYS)
  const funds = useQuery({
    queryKey: ['daily-funds', applied.from, applied.to],
    queryFn: () => backofficeApi.dailyFunds(applied.from, applied.to),
    enabled: rangeReady,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  })
  const accsum = useQuery({
    queryKey: ['daily-accsum', applied],
    queryFn: () => backofficeApi.dailyAccsum(applied.book, applied.from, applied.to),
    enabled: rangeReady,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  })

  const rangeError =
    !draft.from || !draft.to
      ? 'Pick both dates'
      : draft.from > draft.to
        ? 'The end date must be on or after the start date'
        : dayCount(draft) > MAX_DAYS
          ? `Pick at most ${MAX_DAYS} days`
          : undefined
  const dirty = JSON.stringify(draft) !== JSON.stringify(applied)
  const isDefault = JSON.stringify(draft) === JSON.stringify(rangeFromSearch(new URLSearchParams())) && !dirty

  function apply(next: Range) {
    setDraft(next)
    setApplied(next)
    const cur = new URLSearchParams(sp)
    cur.set('from', next.from)
    cur.set('to', next.to)
    cur.set('book', next.book)
    setSp(cur, { replace: true })
  }

  return (
    <PageFrame
      title="Daily"
      description="Approved deposits, withdrawals and transfers per day, and AccSum account totals per day."
    >
      <FilterBarForm
        className={FILTER_BAR}
        onSubmit={() => {
          if (!rangeError) apply(draft)
        }}
      >
        <div className="flex max-lg:flex-col max-lg:gap-3 lg:flex-row lg:flex-wrap lg:items-start lg:gap-3">
          <Field label="From" className="min-w-40">
            <Input type="date" value={draft.from} onChange={(e) => setDraft({ ...draft, from: e.target.value })} />
          </Field>
          <Field label="To" className="min-w-40" error={rangeError}>
            <Input type="date" value={draft.to} onChange={(e) => setDraft({ ...draft, to: e.target.value })} />
          </Field>
          <Field label="Book" className="min-w-32" error={booksQuery.isError ? 'Could not load books' : undefined}>
            <GlassSelect
              value={draft.book}
              onChange={(book) => setDraft({ ...draft, book })}
              options={books.map((b) => ({ value: b.book_id, label: bookLabel(b.book_id) }))}
            />
          </Field>
          <FilterBarApply
            isDirty={dirty && !rangeError}
            canReset={!isDefault}
            onReset={() => apply(rangeFromSearch(new URLSearchParams()))}
          />
        </div>
      </FilterBarForm>

      <section className="space-y-2">
        <h2 className="text-[15px] font-bold">Funds by day</h2>
        <DeskTable
          columns={fundsColumns}
          rows={funds.data?.items ?? []}
          rowKey={(r) => `${r.date}-${r.currency}`}
          loading={funds.isLoading}
          error={funds.error}
          emptyTitle={rangeReady ? 'No approved funds in this period' : BAD_RANGE_TITLE}
          emptyDescription={rangeReady ? 'Grouped by the day they were confirmed (Asia/Jakarta) and by currency. Nothing is converted.' : BAD_RANGE_HINT}
        />
      </section>

      <section className="space-y-2">
        <h2 className="text-[15px] font-bold">Account totals by day</h2>
        <DeskTable
          columns={accsumColumns}
          rows={accsum.data?.items ?? []}
          rowKey={(r) => r.trade_date}
          loading={accsum.isLoading}
          error={accsum.error}
          emptyTitle={rangeReady ? 'No AccSum data in this period' : BAD_RANGE_TITLE}
          emptyDescription={rangeReady ? 'Days without AccSum rows are left out.' : BAD_RANGE_HINT}
        />
      </section>
    </PageFrame>
  )
}
